import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Users, ArrowUpRight, Trophy, Flame } from "lucide-react";
import { getLeaderboard } from "@/lib/api";

const RANK_LABEL = (i) => String(i + 1).padStart(2, "0");

export default function Leaderboard({ variant = "surface" }) {
  const { t } = useTranslation();
  const [leaders, setLeaders] = useState([]);
  useEffect(() => {
    getLeaderboard(10)
      .then((d) => setLeaders(d.leaders || []))
      .catch(() => setLeaders([]));
  }, []);

  if (!leaders.length) return null;

  const isDark = variant === "dark";
  return (
    <section
      className={`py-24 md:py-32 ${isDark ? "bg-forest text-canvas noise" : "bg-surface"}`}
      data-testid="leaderboard"
    >
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-8">
            <div className={`label-eyebrow ${isDark ? "!text-lime" : ""}`}>{t("home.leaderboard.eyebrow")}</div>
            <h2 className={`mt-4 font-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest ${isDark ? "text-canvas" : "text-forest"}`}>
              Top ten who <span className="italic font-serif">brought the room.</span>
            </h2>
          </div>
          <div className={`lg:col-span-4 ${isDark ? "text-canvas/70" : "text-ink/70"}`}>
            The founders pulling the most friends onto Instantxfer CashApp. One referral = one queue-skipped friend. Live-updated.
          </div>
        </div>

        <div className={`rounded-3xl overflow-hidden border ${isDark ? "border-canvas/15 bg-white/[0.03] backdrop-blur-sm" : "border-forest/10 bg-white"}`}>
          <table className="w-full">
            <thead>
              <tr className={`text-[10px] tracking-[0.22em] uppercase ${isDark ? "text-canvas/60" : "text-ink/50"}`}>
                <th className="text-left px-6 py-4 font-normal">Rank</th>
                <th className="text-left px-6 py-4 font-normal">Founder</th>
                <th className="text-left px-6 py-4 font-normal hidden md:table-cell">Handle</th>
                <th className="text-right px-6 py-4 font-normal">Referrals</th>
              </tr>
            </thead>
            <tbody>
              {leaders.map((l, i) => (
                <motion.tr
                  key={`${l.seat}-${i}`}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.035 }}
                  className={`border-t ${isDark ? "border-canvas/10" : "border-forest/8"} group`}
                  data-testid={`leaderboard-row-${i}`}
                >
                  <td className="px-6 py-5 align-middle">
                    <div className="flex items-center gap-3">
                      <span className={`font-mono text-xs ${isDark ? "text-canvas/50" : "text-ink/45"}`}>
                        {RANK_LABEL(i)}
                      </span>
                      {i === 0 && (
                        <Trophy className={`w-4 h-4 ${isDark ? "text-lime" : "text-forest"}`} strokeWidth={1.8} />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-5 align-middle">
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-10 h-10 rounded-full grid place-items-center font-display font-medium text-sm ${
                          i === 0
                            ? "bg-lime text-ink"
                            : isDark
                            ? "bg-canvas/10 text-canvas border border-canvas/20"
                            : "bg-forest text-canvas"
                        }`}
                      >
                        {l.initials}
                      </span>
                      <div>
                        <div className={`font-display text-lg tracking-tight flex items-center gap-2 ${isDark ? "text-canvas" : "text-forest"}`}>
                          Seat {String(l.seat).padStart(3, "0")}
                          {l.has_streak && (
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] tracking-[0.2em] uppercase font-bold ${
                                isDark ? "bg-lime text-ink" : "bg-forest text-lime"
                              }`}
                              data-testid={`leaderboard-streak-${l.seat}`}
                            >
                              <Flame className="w-2.5 h-2.5" strokeWidth={2.2} /> streak
                            </span>
                          )}
                        </div>
                        <div className={`text-xs mt-0.5 max-w-md truncate ${isDark ? "text-canvas/60" : "text-ink/55"}`}>
                          {l.testimonial ? (
                            <span className="italic font-serif">&ldquo;{l.testimonial}&rdquo;</span>
                          ) : (
                            "Founder 100 member"
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5 align-middle hidden md:table-cell">
                    {l.handle ? (
                      <Link
                        to={`/founders/${l.handle}`}
                        className={`link-reveal font-mono text-sm ${isDark ? "text-lime" : "text-forest"}`}
                        data-testid={`leaderboard-handle-${l.handle}`}
                      >
                        @{l.handle}.instant
                      </Link>
                    ) : (
                      <span className={`font-mono text-sm ${isDark ? "text-canvas/40" : "text-ink/35"}`}>
                        — not yet claimed —
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-5 align-middle text-right">
                    <span className={`inline-flex items-center gap-2 font-display text-xl tracking-tight ${isDark ? "text-lime" : "text-forest"}`}>
                      <Users className="w-4 h-4 opacity-70" strokeWidth={1.8} />
                      {l.referrals_count}
                    </span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
          <div className={`text-xs ${isDark ? "text-canvas/60" : "text-ink/55"}`}>
            Updated in real time · Only initials are shown — full names stay private.
          </div>
          <Link
            to="/waitlist"
            className={isDark ? "btn-accent" : "btn-primary"}
            data-testid="leaderboard-cta"
          >
            Take a seat & climb <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
